// ============================================================================
// wcfSelectAll — paginated SELECT helper
// ============================================================================
// Phase 2.0.0: extracted verbatim from main.jsx.
//
// CRITICAL — DO NOT REWRITE (per MIGRATION_PLAN §10):
// Supabase PostgREST silently caps .limit() at 1000 rows. For any table
// that can realistically grow past that (weigh_ins, cattle_dailys, etc.)
// callers MUST use this helper instead of plain .select(). Caller supplies
// a function that builds a query for a given [from, to] range so ordering
// and filters stay intact across pages.
//
// Usage:
//   const all = await wcfSelectAll((from, to) =>
//     sb.from('weigh_ins').select('*').order('entered_at',{ascending:false}).range(from, to)
//   );
// ============================================================================

// What one page of a PostgREST range query resolves to. Only `data` and `error`
// are read here, and an error is treated exactly like an empty page (the loop
// breaks and returns whatever was gathered) — so `error` is `unknown` rather
// than a Supabase error type: this helper never inspects it.
export type RangeQueryResult<Row> = {
  data?: Row[] | null;
  error?: unknown;
};

// `PromiseLike` rather than `Promise` is deliberate: a Supabase query builder is
// a thenable, not a real Promise, and is passed straight through here.
export type BuildRangeQuery<Row> = (from: number, to: number) => PromiseLike<RangeQueryResult<Row>>;

// Generic over the row type so a caller's `.select('*')` row shape survives the
// pagination instead of being flattened.
export async function wcfSelectAll<Row = unknown>(
  buildRangeQuery: BuildRangeQuery<Row>,
  pageSize?: number,
): Promise<Row[]> {
  const page = pageSize || 1000;
  let all: Row[] = [],
    from = 0;
  while (true) {
    const {data, error} = await buildRangeQuery(from, from + page - 1);
    if (error || !data || data.length === 0) break;
    all = all.concat(data);
    if (data.length < page) break;
    from += page;
  }
  return all;
}
